/*
  Self-contained product gallery powered by Splide.
  Replaces Dawn's media-gallery.js entirely — handles slide rendering, thumbnail
  navigation, and main↔thumbnail sync without any Dawn gallery dependency.
  The lightbox (modal-opener / product-modal.js) is independent and untouched.
*/

function findSibling(el, selector) {
  const parent = el.closest('[data-gallery-root]') || el.parentElement;
  return parent ? parent.querySelector(selector) : null;
}

if (!customElements.get('splide-viewer')) {
  customElements.define(
    'splide-viewer',
    class SplideViewer extends HTMLElement {
      connectedCallback() {
        if (this.splide || typeof Splide === 'undefined') {
          if (typeof Splide === 'undefined') {
            window.addEventListener('splide:loaded', () => this.connectedCallback(), { once: true });
          }
          return;
        }

        this.splide = new Splide(this, {
          type: 'slide',
          perPage: 1,
          pagination: true,
          arrows: false,
          drag: true,
          flickPower: 300,
          flickMaxPages: 1,
          speed: 350,
          dragMinThreshold: { mouse: 4, touch: 10 },
          swipeDistanceThreshold: 30,
          keyboard: 'focused',
          mediaQuery: 'min',
          classes: {
            pagination: 'splide__pagination product__media-pagination',
            page: 'splide__pagination__page product__media-pagination__dot',
          },
        });

        const counterCurrent = this.querySelector('.slider-counter--current');
        const counterTotal = this.querySelector('.slider-counter--total');

        const onMoved = () => {
          const index = this.splide.index;
          const slides = this.sliderItems;
          const currentEl = slides[index];
          if (counterCurrent) counterCurrent.textContent = index + 1;
          if (counterTotal) counterTotal.textContent = slides.length;
          slides.forEach((s) => s.classList.remove('is-active'));
          if (currentEl) currentEl.classList.add('is-active');
          this.syncThumbnail(currentEl);
        };

        this.splide.on('mounted', onMoved);
        this.splide.on('moved', onMoved);

        this.splide.mount();
      }

      get sliderItems() {
        return Array.from(this.querySelectorAll(':scope > .splide__track .splide__slide'));
      }

      goToMedia(mediaId) {
        if (!this.splide) return false;
        const index = this.sliderItems.findIndex((slide) => slide.dataset.mediaId === mediaId);
        if (index < 0) return false;
        this.splide.go(index);
        return true;
      }

      syncThumbnail(currentEl) {
        if (!currentEl) return;
        const thumbs = findSibling(this, 'splide-thumbnails');
        if (!thumbs) return;
        const mediaId = currentEl.dataset.mediaId;
        // The thumbnails element may not be upgraded yet (it's defined after
        // this viewer in the same file), so wait for its class before calling.
        customElements.whenDefined('splide-thumbnails').then(() => {
          if (typeof thumbs.setActiveByMediaId === 'function') {
            thumbs.setActiveByMediaId(mediaId);
          }
        });
      }
    }
  );
}

if (!customElements.get('splide-thumbnails')) {
  customElements.define(
    'splide-thumbnails',
    class SplideThumbnails extends HTMLElement {
      connectedCallback() {
        if (this.splide || typeof Splide === 'undefined') {
          if (typeof Splide === 'undefined') {
            window.addEventListener('splide:loaded', () => this.connectedCallback(), { once: true });
          }
          return;
        }

        this.splide = new Splide(this, {
          type: 'slide',
          perPage: 5,
          perMove: 1,
          gap: 16,
          arrows: false,
          pagination: false,
          drag: true,
          keyboard: false,
        });

        this.splide.mount();

        // Clicking a thumbnail navigates the main viewer.
        this.querySelectorAll('[data-target]').forEach((item) => {
          const button = item.querySelector('button');
          if (!button) return;
          button.addEventListener('click', (e) => {
            e.preventDefault();
            const viewer = findSibling(this, 'splide-viewer');
            if (viewer) viewer.goToMedia(item.dataset.target);
          });
        });
      }

      get thumbItems() {
        return Array.from(this.querySelectorAll(':scope > .splide__track [data-target]'));
      }

      setActiveByMediaId(mediaId) {
        if (!this.splide) return;
        const items = this.thumbItems;
        const index = items.findIndex((el) => el.dataset.target === mediaId);
        if (index < 0) return;
        items.forEach((el) => el.querySelector('button')?.removeAttribute('aria-current'));
        items[index].querySelector('button')?.setAttribute('aria-current', 'true');
        // Scroll the thumb into view if it's outside the visible window.
        const first = this.splide.index;
        const perPage = this.splide.options.perPage;
        if (index < first || index >= first + perPage) this.splide.go(index);
      }
    }
  );
}

// Signal once Splide is loaded (defer order isn't guaranteed across scripts).
(function () {
  function ready() {
    window.dispatchEvent(new CustomEvent('splide:loaded'));
  }
  if (typeof Splide !== 'undefined') {
    ready();
  } else {
    const check = setInterval(() => {
      if (typeof Splide !== 'undefined') {
        clearInterval(check);
        ready();
      }
    }, 50);
  }
})();
